import type { TFunction } from "i18next";
import * as XLSX from "xlsx";
import { z } from "zod";
import {
  EXCEL_FILE_ACCEPTED_EXTENSIONS,
  EXCEL_FILE_ACCEPTED_MIME_TYPES,
  EXCEL_FILE_MAX_SIZE,
  EXCEL_REQUIRED_COLUMNS,
  EXCEL_SUMMARY_REQUIRED_TOTAL_ROWS,
} from "@/constants/validation";

export const getSummarySchema = (t: TFunction) => {
  const fileSchema = z
    .instanceof(File, { message: t("summary.file.validation.required") })
    .refine(file => file.size > 0, t("summary.file.validation.required"))
    .refine(
      file => EXCEL_FILE_ACCEPTED_EXTENSIONS.some(ext => file.name.toLowerCase().endsWith(ext)),
      t("summary.file.validation.invalidExtension", {
        allowed: EXCEL_FILE_ACCEPTED_EXTENSIONS.join(", "),
      }),
    )
    .refine(
      file => EXCEL_FILE_ACCEPTED_MIME_TYPES.includes(file.type),
      t("summary.file.validation.invalidType", {
        allowed: EXCEL_FILE_ACCEPTED_MIME_TYPES.join(", "),
      }),
    )

    .refine(file => file.size <= EXCEL_FILE_MAX_SIZE, t("summary.file.validation.fileTooLarge"));

  return z
    .object({
      excelProfile: fileSchema.optional().nullable(),
    })
    .superRefine(async (data, ctx) => {
      const file = data.excelProfile;
      if (!file) return;

      const hasFormatError =
        !EXCEL_FILE_ACCEPTED_EXTENSIONS.some(ext => file.name.toLowerCase().endsWith(ext)) ||
        !EXCEL_FILE_ACCEPTED_MIME_TYPES.includes(file.type);
      if (hasFormatError) return;

      try {
        const buffer = await file.arrayBuffer();
        const workbook = XLSX.read(buffer);
        const sheet = workbook.Sheets[workbook.SheetNames[0]];

        const allRows = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1 });
        const nonEmptyRows = allRows.filter(row =>
          row.some(cell => cell !== null && cell !== undefined && cell !== ""),
        );
        const numRows = nonEmptyRows.length;
        const numCols =
          nonEmptyRows.length > 0 ? Math.max(...nonEmptyRows.map(r => r.length)) : 0;

        if (numCols !== EXCEL_REQUIRED_COLUMNS) {
          ctx.addIssue({
            code: "custom",
            path: ["excelProfile"],
            message: t("summary.file.validation.invalidColumnCount", {
              required: EXCEL_REQUIRED_COLUMNS,
            }),
          });
        }

        if (numCols === EXCEL_REQUIRED_COLUMNS && numRows > 0 && nonEmptyRows[0][1] !== "P_demand") {
          ctx.addIssue({
            code: "custom",
            path: ["excelProfile"],
            message: t("summary.file.validation.invalidSecondColumnName"),
          });
        }

        if (numRows !== EXCEL_SUMMARY_REQUIRED_TOTAL_ROWS) {
          ctx.addIssue({
            code: "custom",
            path: ["excelProfile"],
            message: t("summary.file.validation.invalidRowCount", {
              required: EXCEL_SUMMARY_REQUIRED_TOTAL_ROWS,
            }),
          });
        }
      } catch {
        ctx.addIssue({
          code: "custom",
          path: ["excelProfile"],
          message: t("summary.file.validation.required"),
        });
      }
    });
};

export type SummarySchema = z.infer<ReturnType<typeof getSummarySchema>>;
